import {
  ArrowRight,
  Calendar,
  ExternalLink,
  Globe,
  Linkedin,
  Mail,
  Phone,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatDate, initials } from "@/lib/formatters";
import { getEnrichmentColor, scoreColor } from "@/lib/constants";
import {
  COPIED_FROM_COMPANY_LABEL,
  formatPhone,
  isPhoneNumber,
  phoneCopiedFromCompany,
  phoneForCopy,
  phoneHref,
} from "@/lib/phone";
import { CopyButton } from "@/components/shared/CopyButton";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import type { Company, Contact } from "@/api/types";

interface ContactDetailSheetProps {
  contact: Contact | null;
  company: Company | null;
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  /** When provided, renders a destructive "Delete" button in the footer. */
  onDelete?: () => void;
  readOnly?: boolean;
  /** When true, the sheet is opened from the company detail page itself —
   * the "View company" CTA is hidden and the footer uses Close instead. */
  inCompanyContext?: boolean;
}

export function ContactDetailSheet({
  contact,
  company,
  projectSlug,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  readOnly = false,
  inCompanyContext = false,
}: ContactDetailSheetProps) {
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-[420px]">
        <SheetHeader>
          <SheetTitle>Contact detail</SheetTitle>
          <SheetDescription className="sr-only">
            Read-only contact card with quick actions.
          </SheetDescription>
        </SheetHeader>

        {contact && (
          <>
            <div className="flex-1 space-y-6 overflow-y-auto py-4">
              {/* Identity */}
              <div className="flex flex-col items-center text-center">
                <Avatar className="h-14 w-14 border border-slate-200">
                  <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-500 text-base font-semibold">
                    {initials(contact.full_name)}
                  </AvatarFallback>
                </Avatar>
                <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900">
                  {contact.full_name}
                </h2>
                {contact.title && (
                  <p className="text-sm text-slate-500">{contact.title}</p>
                )}
                {contact.is_decision_maker && (
                  <span className="mt-2 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                    Decision maker
                  </span>
                )}
              </div>

              {/* Contact info */}
              <Section title="Contact info">
                <ContactRow
                  icon={Mail}
                  label="Email"
                  value={contact.email}
                  href={contact.email ? `mailto:${contact.email}` : null}
                  hidden={isHidden(contact, "email")}
                />
                <ContactRow
                  icon={Phone}
                  label="Phone"
                  value={isPhoneNumber(contact.phone) ? contact.phone : null}
                  displayValue={formatPhone(contact.phone)}
                  copyValue={isPhoneNumber(contact.phone) ? phoneForCopy(contact.phone) : null}
                  href={phoneHref(contact.phone)}
                  hidden={isHidden(contact, "phone")}
                  note={phoneCopiedFromCompany(contact) ? COPIED_FROM_COMPANY_LABEL : null}
                />
                <ContactRow
                  icon={Linkedin}
                  label="LinkedIn"
                  value={contact.linkedin_url}
                  displayValue={
                    contact.linkedin_url
                      ? contact.linkedin_url.replace(/^https?:\/\/(www\.)?/, "")
                      : null
                  }
                  href={contact.linkedin_url}
                  external
                />
              </Section>

              {/* Details */}
              <Section title="Details">
                <InfoRow icon={Globe} label="Source">
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium capitalize text-slate-700">
                    {contact.source}
                  </span>
                </InfoRow>
                <InfoRow icon={ShieldCheck} label="Enrichment">
                  <EnrichmentBadge status={contact.enrichment_status} />
                </InfoRow>
                <InfoRow icon={Calendar} label="Added">
                  <span className="text-sm text-slate-700">
                    {formatDate(contact.created_at)}
                  </span>
                </InfoRow>
              </Section>

              {/* Company */}
              {company && (
                <Section title="Company">
                  <div className="rounded-xl border border-slate-200/70 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-slate-900">
                          {company.name}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          {company.sector && <span>{company.sector}</span>}
                          {company.sector && company.domain && (
                            <span className="text-slate-300">·</span>
                          )}
                          {company.domain && <span>{company.domain}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <span
                          className={cn(
                            "text-base font-semibold",
                            scoreColor(company.effective_score),
                          )}
                        >
                          {company.effective_score ?? "—"}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">
                          Score
                        </span>
                      </div>
                    </div>
                    {!inCompanyContext && (
                      <button
                        type="button"
                        onClick={() => {
                          onOpenChange(false);
                          navigate(
                            `/projects/${projectSlug}/companies/${company.id}`,
                          );
                        }}
                        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        View company page
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </Section>
              )}
            </div>

            <SheetFooter className="flex-col gap-2 border-t border-slate-900/[0.06] pt-4 sm:flex-col sm:gap-2 sm:space-x-0">
              {(!readOnly || onDelete) && (
                <div className="flex w-full gap-2">
                  {!readOnly && (
                    <Button
                      variant="outline"
                      onClick={onEdit}
                      className="flex-1"
                    >
                      Edit
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="outline"
                      onClick={onDelete}
                      className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              )}
              {inCompanyContext ? (
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  className="w-full"
                >
                  Close
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!company) return;
                    onOpenChange(false);
                    navigate(
                      `/projects/${projectSlug}/companies/${company.id}`,
                    );
                  }}
                  disabled={!company}
                  className="w-full"
                >
                  View company
                </Button>
              )}
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

interface ContactRowProps {
  icon: LucideIcon;
  label: string;
  value: string | null;
  displayValue?: string | null;
  /** What the copy button copies, when it is not `value`. */
  copyValue?: string | null;
  href: string | null;
  external?: boolean;
  /** Withheld from this reader outside their call session and their called companies (`hidden_details`). */
  hidden?: boolean;
  /** A short provenance line under the value (e.g. a number copied from the company's). */
  note?: string | null;
}

function ContactRow({
  icon: Icon,
  label,
  value,
  displayValue,
  copyValue,
  href,
  external,
  hidden,
  note,
}: ContactRowProps) {
  const shown = displayValue ?? value;
  const isMissing = !value;

  return (
    <div className="flex items-center gap-3">
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {label}
        </div>
        {hidden ? (
          <HiddenDetail label={label === "Email" ? "Email" : "Phone"} className="text-sm" />
        ) : isMissing ? (
          <div className="text-sm text-slate-400">—</div>
        ) : href ? (
          <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noreferrer" : undefined}
            className="block truncate font-mono text-sm text-indigo-600 hover:text-indigo-500"
            title={value ?? undefined}
          >
            {shown}
            {external && (
              <ExternalLink className="ml-1 inline h-3 w-3 text-slate-400" />
            )}
          </a>
        ) : (
          <div className="truncate font-mono text-sm text-slate-700">
            {shown}
          </div>
        )}
        {note && !hidden && !isMissing && (
          <div className="text-[11px] italic text-slate-400">{note}</div>
        )}
      </div>
      {!isMissing && value && (
        <CopyButton
          value={copyValue ?? value}
          label={label === "Phone" ? "phone number" : label === "Email" ? "email" : label}
        />
      )}
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function EnrichmentBadge({ status }: { status: string }) {
  const colors = getEnrichmentColor(status);
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-1.5 py-0.5 text-xs font-medium capitalize",
        colors.bg,
        colors.text,
        colors.border,
      )}
    >
      {status}
    </span>
  );
}
